/**
 * Écran « Réglages DTS » : choix de la ristourne allocations familiales (CNSS) et du plafond CNAMGS.
 * Ces deux choix dépendent d'une décision de la direction ; les valeurs par défaut sont les plus prudentes
 * (aucune déduction ; assiette plafonnée comme sur le bulletin de paie).
 */
import { esc, fmtFCFA } from '@presentation/shell/format';
import { PLAFOND_CNAMGS } from '@shared/constants/Taux';
import {
  PARAMETRES_DTS_PAR_DEFAUT,
  ParametresDtsInvalideError,
  type OptionPlafondCnamgs,
  type OptionRistourneAf,
  type ParametresDts,
  type SaisieParametresDts,
} from '@domain/fiscalite/DtsParametres';
import { STYLE_AIDE, erreurChampHtml, majErreurChamp, majResume, majStatut, type Notifier } from '@presentation/views/comptabilite/panelKit';

export const DESCRIPTION_AUDIT_DTS = 'Réglages des déclarations DTS (ristourne allocations familiales, plafond CNAMGS)';

export interface DtsParametresDeps {
  canMutate: boolean;
  load(): Promise<ParametresDts>;
  save(saisie: SaisieParametresDts): Promise<ParametresDts>;
  audit(description: string, payload?: Record<string, unknown>): Promise<void>;
  notify: Notifier;
  onChange?(parametres: ParametresDts): void;
}

const P = 'dts-param';

const OPTIONS_AF: { valeur: OptionRistourneAf; libelle: string; aide: string }[] = [
  { valeur: 'A', libelle: 'Aucune déduction', aide: 'Réglage prudent : la cotisation CNSS à payer est la cotisation brute.' },
  { valeur: 'B', libelle: 'Ristourne de 2 % de la masse salariale plafonnée CNSS', aide: 'La ristourne est calculée automatiquement et déduite de la cotisation CNSS.' },
  { valeur: 'C', libelle: 'Déduire le montant réel des allocations familiales versées', aide: 'Vous saisissez le montant versé sur le trimestre ; il est déduit de la cotisation CNSS.' },
];

const OPTIONS_PLAFOND: { valeur: OptionPlafondCnamgs; libelle: string; aide: string }[] = [
  {
    valeur: 'A',
    libelle: `Plafonné à ${fmtFCFA(PLAFOND_CNAMGS)} par salarié et par mois`,
    aide: 'Réglage prudent : même base que sur le bulletin de paie.',
  },
  {
    valeur: 'B',
    libelle: 'Non plafonné (seulement si la CNAMGS vous le confirme)',
    aide: 'Attention : au-dessus du plafond, la base retenue est le brut imposable du bulletin (valeur approchée).',
  },
];

export async function mountDtsParametresPanel(host: HTMLElement, deps: DtsParametresDeps): Promise<void> {
  let courant = await deps.load();

  const render = (): void => {
    const af = courant.ristourneAf.option;
    const plafond = courant.plafondCnamgs.option;
    const radios = <T extends string>(
      nom: string,
      options: { valeur: T; libelle: string; aide: string }[],
      choisi: T,
    ): string =>
      options
        .map(
          (o) => `<div style="margin:6px 0;">
            <label style="display:flex;gap:8px;align-items:flex-start;font-size:13px;">
              <input type="radio" name="${nom}" value="${o.valeur}" ${o.valeur === choisi ? 'checked' : ''} ${deps.canMutate ? '' : 'disabled'}
                aria-describedby="${nom}-${o.valeur}-aide" style="margin-top:3px;">
              <span><strong>${esc(o.libelle)}</strong><br>
                <span id="${nom}-${o.valeur}-aide" style="font-size:12px;color:var(--ardoise);">${esc(o.aide)}</span></span>
            </label></div>`,
        )
        .join('');
    host.innerHTML = `
      <div class="card" id="${P}-panel" aria-labelledby="${P}-titre">
        <h2 class="card-title" id="${P}-titre">Réglages des déclarations DTS (CNSS et CNAMGS)</h2>
        <p style="${STYLE_AIDE}">Ces deux choix dépendent d’une décision de la direction. Tant qu’elle n’est pas prise, gardez les réglages prudents : ils s’appliquent aux fichiers DTS exportés depuis cet écran.
          ${deps.canMutate ? '' : '<strong>Lecture seule : seuls l’administrateur et le gestionnaire RH peuvent modifier ces réglages.</strong>'}</p>
        <div id="${P}-resume" class="form-error-summary" role="alert" hidden></div>
        <fieldset style="border:1px solid #DAE0E4;border-radius:6px;padding:10px 14px;margin:0 0 12px;">
          <legend style="font-weight:700;color:var(--marine);padding:0 6px;">Ristourne « allocations familiales » (déclaration CNSS)</legend>
          ${radios('dts-af', OPTIONS_AF, af)}
          <div class="field" id="${P}-montant-zone" style="max-width:320px;margin:8px 0 0 24px;" ${af === 'C' ? '' : 'hidden'}>
            <label for="${P}-montant">Montant des allocations familiales versées sur le trimestre (FCFA)</label>
            <input id="${P}-montant" type="number" min="0" step="1" inputmode="numeric" value="${courant.ristourneAf.montantAllocationsFamilialesSaisi}"
              aria-describedby="${P}-montant-aide ${P}-err-montant" ${deps.canMutate ? '' : 'readonly'}>
            <span id="${P}-montant-aide" style="font-size:12px;color:var(--ardoise);">À ressaisir à chaque trimestre.</span>
            ${erreurChampHtml(`${P}-err-montant`, undefined)}
          </div>
        </fieldset>
        <fieldset style="border:1px solid #DAE0E4;border-radius:6px;padding:10px 14px;margin:0 0 12px;">
          <legend style="font-weight:700;color:var(--marine);padding:0 6px;">Plafond de la base CNAMGS (déclaration CNAMGS)</legend>
          ${radios('dts-plafond', OPTIONS_PLAFOND, plafond)}
        </fieldset>
        <p id="${P}-statut" role="status" style="${STYLE_AIDE}"></p>
        ${
          deps.canMutate
            ? `<div style="display:flex;gap:10px;flex-wrap:wrap;">
                 <button type="button" class="btn btn-primary" id="${P}-enregistrer">Enregistrer les réglages DTS</button>
                 <button type="button" class="btn btn-secondary" id="${P}-defaut">Revenir aux réglages prudents</button>
               </div>`
            : ''
        }
      </div>`;
    bind();
  };

  const lire = (): SaisieParametresDts => {
    const af = host.querySelector<HTMLInputElement>('input[name="dts-af"]:checked')?.value;
    const plafond = host.querySelector<HTMLInputElement>('input[name="dts-plafond"]:checked')?.value;
    const montant = Number(host.querySelector<HTMLInputElement>(`#${P}-montant`)?.value || 0);
    return {
      ristourneAf: { ...(af ? { option: af } : {}), montantAllocationsFamilialesSaisi: montant },
      plafondCnamgs: plafond ? { option: plafond } : {},
    };
  };

  const bind = (): void => {
    host.querySelectorAll<HTMLInputElement>('input[name="dts-af"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const zone = host.querySelector<HTMLElement>(`#${P}-montant-zone`);
        if (zone) zone.hidden = radio.value !== 'C';
        majResume(host.querySelector<HTMLElement>(`#${P}-resume`), null);
        if (radio.value === 'C') host.querySelector<HTMLInputElement>(`#${P}-montant`)?.focus();
      });
    });
    host.querySelector(`#${P}-enregistrer`)?.addEventListener('click', () => void enregistrer(lire()));
    host.querySelector(`#${P}-defaut`)?.addEventListener('click', () => {
      void enregistrer({
        ristourneAf: { option: PARAMETRES_DTS_PAR_DEFAUT.ristourneAf.option, montantAllocationsFamilialesSaisi: 0 },
        plafondCnamgs: { option: PARAMETRES_DTS_PAR_DEFAUT.plafondCnamgs.option },
      });
    });
  };

  const enregistrer = async (saisie: SaisieParametresDts): Promise<void> => {
    if (!deps.canMutate) return;
    const resume = host.querySelector<HTMLElement>(`#${P}-resume`);
    majErreurChamp(host, host.querySelector<HTMLInputElement>(`#${P}-montant`), `${P}-err-montant`, undefined);
    const avant = courant;
    try {
      courant = await deps.save(saisie);
    } catch (err) {
      if (err instanceof ParametresDtsInvalideError) {
        if (/montant/i.test(err.message)) {
          majErreurChamp(host, host.querySelector<HTMLInputElement>(`#${P}-montant`), `${P}-err-montant`, err.message);
          host.querySelector<HTMLInputElement>(`#${P}-montant`)?.focus();
        } else {
          majResume(resume, err.message);
        }
        return;
      }
      deps.notify.error(err instanceof Error ? err.message : 'Enregistrement impossible.');
      return;
    }
    await deps.audit(DESCRIPTION_AUDIT_DTS, {
      avant: { ristourne_af: avant.ristourneAf.option, plafond_cnamgs: avant.plafondCnamgs.option },
      apres: {
        ristourne_af: courant.ristourneAf.option,
        montant_af: courant.ristourneAf.montantAllocationsFamilialesSaisi,
        plafond_cnamgs: courant.plafondCnamgs.option,
      },
    });
    deps.onChange?.(courant);
    render();
    host.querySelector<HTMLElement>(`#${P}-enregistrer`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`), 'Réglages DTS enregistrés.');
    deps.notify.success('Réglages DTS enregistrés. Ils s’appliquent aux prochains exports.');
  };

  render();
}
